import { Badge } from '@workspace/ui/components/badge'
import { ArrowDownLeft, ArrowUpRight, Coins, Wallet } from 'lucide-react'
import { InvestorLayout } from '../../components/investor-layout'
import { useInvestorContext } from '../../contexts/investor-guard'
import { useInvestorCreditsQuery } from '../../hooks/api'

export function CreditsPage() {
  const { investor } = useInvestorContext()
  const { data: credits, isPending } = useInvestorCreditsQuery(investor.id)

  const balance = credits?.balance
  const transactions = credits?.transactions

  return (
    <InvestorLayout activeNav="Credits">
      <div className="mx-auto max-w-2xl space-y-8 py-8">
        {/* Balance */}
        <div className="rounded-2xl border border-border bg-card/50 p-5">
          <div className="flex items-center gap-4">
            <div className="flex size-12 items-center justify-center rounded-xl bg-secondary">
              <Wallet className="size-6 text-[#f97316]" />
            </div>
            <div>
              {balance !== undefined ? (
                <>
                  <p className="text-3xl font-bold tabular-nums text-white">{balance}</p>
                  <p className="text-xs text-muted-foreground">Credits available</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Loading...</p>
              )}
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Transaction History
          </h3>

          {isPending ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>
          ) : transactions && transactions.length > 0 ? (
            <div className="overflow-hidden rounded-2xl border border-border bg-card/50">
              <div className="divide-y divide-border">
                {transactions.map((tx) => {
                  const isGrant = tx.amount > 0
                  return (
                    <div
                      key={tx.id}
                      className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-secondary/30"
                    >
                      <div
                        className={`flex size-8 shrink-0 items-center justify-center rounded-full ${isGrant ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}
                      >
                        {isGrant ? (
                          <ArrowDownLeft className="size-4 text-emerald-400" />
                        ) : (
                          <ArrowUpRight className="size-4 text-red-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-white">
                            {tx.type === 'ADMIN_TOPUP' ? 'Credit Grant' : 'Pitch Session'}
                          </p>
                          <Badge variant={isGrant ? 'success' : 'secondary'} size="sm">
                            {isGrant ? 'Grant' : 'Usage'}
                          </Badge>
                        </div>
                        {tx.note && (
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">{tx.note}</p>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <p
                          className={`text-sm font-semibold tabular-nums ${isGrant ? 'text-emerald-400' : 'text-red-400'}`}
                        >
                          {isGrant ? `+${tx.amount}` : tx.amount}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(tx.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center rounded-2xl border border-border bg-card/50 p-12 text-center">
              <div className="mb-5 flex size-16 items-center justify-center rounded-2xl bg-secondary">
                <Coins className="size-7 text-muted-foreground" />
              </div>
              <h2 className="mb-1 text-lg font-semibold text-white">No transactions yet</h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                Your credit transactions will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </InvestorLayout>
  )
}
